import { useChatActions, useChatStore, useChatStoreApi } from '@ai-sdk-tools/store';
import { useMutation } from '@apollo/client';
import { useAnalytics } from '@newfront-insurance/data-layer-client';
import type { ToolUIPart } from 'ai';
import { useRef, useState } from 'react';

import { getOutlookDraftState, type OutlookDraftAction } from './state';
import type { UIMessageWithMetadata } from '../../../../lib/types';
import { CREATE_OUTLOOK_DRAFT_FROM_PREVIEW } from '../../../../queries/outlook-draft-mutations';
import { NOVA_ANALYTICS_EVENTS, trackNovaEvent } from '../../lib/analytics';
import { normalizeChatUuid } from '../../lib/notification-helpers';

const CREATE_ERROR = "We couldn't create this draft in Outlook. Try again.";
const RECEIPT_ERROR = "We couldn't display the saved draft. Refresh the chat before trying again.";

export function useOutlookDraft(chatUuid: string, messageUuid: string, part: ToolUIPart): OutlookDraftAction {
  const { setMessages } = useChatActions<UIMessageWithMetadata>();
  const chatStore = useChatStoreApi<UIMessageWithMetadata>();
  const analytics = useAnalytics();
  const creatingOrCreated = useRef(false);
  const [error, setError] = useState<string>();
  const isStreamingThisMessage = useChatStore(
    (store) =>
      (store.status === 'streaming' || store.status === 'submitted') && store.messages.at(-1)?.id === messageUuid,
  );
  const state = getOutlookDraftState(part);
  const canCreate = part.state === 'output-available' && !state && !isStreamingThisMessage && error !== RECEIPT_ERROR;
  const [createDraft, { loading }] = useMutation(CREATE_OUTLOOK_DRAFT_FROM_PREVIEW, {
    errorPolicy: 'none',
    context: { notifications: { preventErrorNotification: true } },
  });

  const create = async () => {
    const currentPart = chatStore
      .getState()
      .messages.find((message) => message.id === messageUuid)
      ?.parts.find((candidate) => 'toolCallId' in candidate && candidate.toolCallId === part.toolCallId);
    if (!canCreate || loading || creatingOrCreated.current || getOutlookDraftState(currentPart)) return;
    creatingOrCreated.current = true;
    setError(undefined);
    trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.EMAIL_DRAFT_CREATE_OUTLOOK_STARTED);
    try {
      const { data } = await createDraft({
        variables: {
          input: { chatUuid: normalizeChatUuid(chatUuid), messageUuid, toolCallId: part.toolCallId },
        },
      });
      const parts = data?.createOutlookDraftFromPreview?.parts;
      if (
        !Array.isArray(parts) ||
        !parts.every((candidate) => candidate && typeof candidate.type === 'string') ||
        !parts.some((candidate) => candidate.toolCallId === part.toolCallId && getOutlookDraftState(candidate))
      ) {
        setError(RECEIPT_ERROR);
        return;
      }
      setMessages(
        chatStore.getState().messages.map((message) => (message.id === messageUuid ? { ...message, parts } : message)),
      );
      trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.EMAIL_DRAFT_CREATE_OUTLOOK_SUCCEEDED);
    } catch {
      setError(CREATE_ERROR);
      trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.EMAIL_DRAFT_CREATE_OUTLOOK_FAILED);
      creatingOrCreated.current = false;
    }
  };
  return { state, canCreate, pending: loading, error, create };
}
